'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import isBrowser from '../../helpers/is-browser';
import { AnalyticsProvider } from '../analytics-provider';
import { useLocation } from '../location-provider';
export const ParamsContext = createContext({
    addParam: function () {
        throw new Error('Function "addParam" not implemented.');
    },
    addMultipleParams: function () {
        throw new Error('Function "addMultipleParams" not implemented.');
    },
    addParamsToURL: function () {
        throw new Error('Function "addParamsToURL" not implemented.');
    },
    params: undefined,
    getParam: function () {
        throw new Error('Function "getParam" not implemented.');
    },
    getArrayParam: function () {
        throw new Error('Function "getArrayParam" not implemented.');
    },
    removeParam: function () {
        throw new Error('Function "removeParam" not implemented.');
    },
    clearAllParams: function () {
        throw new Error('Function "clearAllParams" not implemented.');
    },
    paramsIsLoading: false,
    addToLocalStorage: function () {
        throw new Error('Function "addToLocalStorage" not implemented.');
    },
    removeFromLocalStorage: function () {
        throw new Error('Function "removeFromLocalStorage" not implemented.');
    },
    getLocalStorageKey: function () {
        throw new Error('Function "getLocalStorageKey" not implemented.');
    },
});
ParamsContext.displayName = 'ParamsContext';
export const ParamsProvider = ({ children }) => {
    const { navigate, search, pathname } = useLocation();
    const [data, setData] = useState({});
    const [paramsPathname, setParamsPathname] = useState('');
    const isInitialized = useRef(false);
    const [localStorageKeys, setLocalStorageKeys] = useState([]);
    useEffect(() => {
        if (!isBrowser)
            return;
        localStorage.setItem('localStorageKeys', JSON.stringify(localStorageKeys));
    }, [localStorageKeys]);
    const addToLocalStorage = useCallback((key, value) => {
        if (!isBrowser)
            return;
        localStorage.setItem(key, value);
        setLocalStorageKeys((prev) => [...prev, key]);
    }, []);
    const removeFromLocalStorage = useCallback((key) => {
        if (!isBrowser)
            return;
        localStorage.removeItem(key);
        setLocalStorageKeys((prev) => prev.filter((k) => k !== key));
    }, []);
    const getLocalStorageKey = useCallback((key) => {
        if (!isBrowser)
            return;
        return localStorage.getItem(key);
    }, []);
    const getParam = useCallback((key) => {
        return (data === null || data === void 0 ? void 0 : data[key]) || '';
    }, [data]);
    const getArrayParam = useCallback((key) => getParam(key).split(',').filter(Boolean), [getParam]);
    const clearAllParams = useCallback((additionalParams) => {
        if (!isBrowser)
            return;
        const params = new URLSearchParams();
        if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
                if (value !== undefined && value !== 'undefined' && value !== '') {
                    params.set(key, value);
                }
            });
        }
        localStorageKeys.forEach((key) => removeFromLocalStorage(key));
        const queryString = params.toString();
        const newUrl = `${pathname}${queryString ? `?${queryString}` : ''}`;
        setData(additionalParams || {});
        if (isBrowser) {
            window.history.replaceState(null, '', newUrl);
        }
        navigate(newUrl, { replace: true });
    }, [localStorageKeys, navigate, pathname, removeFromLocalStorage]);
    const addParam = useCallback((key, value, inLocalStorage = false) => {
        if (!isBrowser)
            return;
        if (!key)
            return;
        setData((prevData) => (Object.assign(Object.assign({}, prevData), { [key]: value })));
        if (inLocalStorage)
            addToLocalStorage(key, value);
    }, [addToLocalStorage]);
    const addMultipleParams = useCallback((params, inLocalStorage = false) => {
        if (!isBrowser)
            return;
        setData((prevData) => {
            const newParams = Object.assign(Object.assign({}, prevData), params);
            const filteredParams = Object.fromEntries(Object.entries(newParams).map(([key, value]) => {
                if (value !== undefined &&
                    value !== 'undefined' &&
                    value !== null) {
                    return [key, value];
                }
                return [key, ''];
            }));
            Object.entries(filteredParams).forEach(([key, value]) => {
                if (inLocalStorage)
                    addToLocalStorage(key, value);
            });
            return filteredParams;
        });
    }, [addToLocalStorage]);
    const addParamsToURL = useCallback(() => {
        if (!isInitialized.current)
            return;
        if (!isBrowser)
            return;
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(data)) {
            if (value !== undefined &&
                value !== 'undefined' &&
                value !== null &&
                value !== '') {
                params.set(key, value);
            }
            else {
                params.delete(key);
            }
        }
        const hash = isBrowser ? window.location.hash || '' : '';
        const queryString = params.toString();
        const url = `${queryString && `?${queryString}`}${hash}`;
        const currentUrl = isBrowser
            ? `${window.location.search}${window.location.hash}`
            : '';
        if (url !== currentUrl) {
            if (!!(url === null || url === void 0 ? void 0 : url.length)) {
                navigate(url, { replace: true });
            }
            else {
                navigate(window.location.pathname, { replace: true });
            }
        }
    }, [data, navigate]);
    const addSearchToParams = useCallback(() => {
        const isFirstInitialization = !isInitialized.current;
        try {
            if (!isBrowser)
                return;
            if (!isFirstInitialization && paramsPathname === pathname)
                return;
            const params = new URLSearchParams(search);
            const entries = Array.from(params.entries());
            const filteredEntries = entries.filter(([_, value]) => value !== '');
            setData(Object.fromEntries(filteredEntries));
            if (isFirstInitialization) {
                const newKeys = localStorage.getItem('localStorageKeys');
                if (newKeys) {
                    setLocalStorageKeys(JSON.parse(newKeys));
                }
            }
        }
        catch (error) {
            console.error('Error parsing URL parameters:', error);
            setData({});
        }
        finally {
            isInitialized.current = true;
            setParamsPathname(pathname);
        }
    }, [paramsPathname, pathname, search]);
    useEffect(() => {
        if (!isBrowser)
            return;
        addSearchToParams();
    }, [addSearchToParams]);
    useEffect(() => {
        var _a;
        if (!isInitialized.current)
            return;
        if (!data)
            return;
        if (((_a = Object.keys(data)) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            addParamsToURL();
        }
    }, [addParamsToURL, data]);
    const removeParam = useCallback((key) => {
        if (!isBrowser)
            return;
        const params = new URLSearchParams(search);
        if (!key || !data[key])
            return;
        setData((prevData) => {
            const _a = prevData, _b = key, _ = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
            return rest;
        });
        params.delete(key);
        const newUrl = params.toString() ? `?${params.toString()}` : '';
        if (newUrl !== '') {
            navigate(newUrl, { replace: true });
        }
        else {
            navigate(pathname, { replace: true });
        }
        if (localStorageKeys.includes(key))
            removeFromLocalStorage(key);
    }, [
        data,
        localStorageKeys,
        navigate,
        pathname,
        removeFromLocalStorage,
        search,
    ]);
    useEffect(() => {
        if (!isBrowser)
            return;
        const firstPageUrl = localStorage.getItem('first_page_url');
        const currentUrl = isBrowser
            ? window.location.href.replace(window.location.origin, '')
            : '';
        if (!firstPageUrl &&
            !['/', '/signup'].includes(pathname) &&
            typeof currentUrl !== 'undefined') {
            localStorage.setItem('first_page_url', currentUrl);
        }
    }, [pathname]);
    useEffect(() => {
        if (!isBrowser)
            return;
        const conversationPageUrl = localStorage.getItem('conversation_page_url');
        const currentUrl = isBrowser
            ? window.location.href.replace(window.location.origin, '')
            : '';
        if (conversationPageUrl !== currentUrl &&
            typeof currentUrl !== 'undefined') {
            localStorage.setItem('conversation_page_url', currentUrl);
        }
    }, [pathname]);
    const contextValue = useMemo(() => ({
        addParam,
        addMultipleParams,
        getParam,
        getArrayParam,
        removeParam,
        clearAllParams,
        params: data,
        addParamsToURL,
        paramsIsLoading: paramsPathname !== pathname,
        addToLocalStorage,
        removeFromLocalStorage,
        getLocalStorageKey,
    }), [
        addParam,
        addMultipleParams,
        getParam,
        getArrayParam,
        removeParam,
        clearAllParams,
        data,
        addParamsToURL,
        paramsPathname,
        pathname,
        addToLocalStorage,
        removeFromLocalStorage,
        getLocalStorageKey,
    ]);
    return (_jsx(ParamsContext.Provider, { value: contextValue, children: _jsx(AnalyticsProvider, { children: children }) }));
};
export const useParams = () => useContext(ParamsContext);
export default ParamsProvider;
